import { generatePath } from '@/services/router/generate-path';
import type { AttributeScope } from '@/services/db/data-index/entity';
import type { DataIndexItemKind, DataIndexType } from './utils/types';

export { asDataIndexType } from './utils/types';

/**
 * The data index's URLs, and the correspondence between the browser a URL
 * names and the endpoints that serve it.
 *
 * The list is served at two spellings while the migration runs: the migrated
 * `/data-index`, and the `journeys/data-index` the embedded app answers. An
 * item's own page is still the embedded app's.
 */

export function dataIndexListPath(workspaceId: string, type: DataIndexListType): string {
  return generatePath('/workspaces/:workspaceId/data-index/:type', { workspaceId, type });
}

export function legacyDataIndexListPath(workspaceId: string, type: DataIndexType): string {
  return generatePath('/workspaces/:workspaceId/journeys/data-index/:type', { workspaceId, type });
}

/**
 * An item's own page, still served by the embedded app: two levels for a
 * person attribute or event, three for an object or relationship attribute,
 * whose name only means something under its object type.
 *
 * The name is customer-authored and can contain anything, including the
 * characters that would otherwise end the segment, so it is escaped here —
 * `generatePath` substitutes params verbatim.
 */
export function legacyDataIndexItemPath(
  workspaceId: string,
  kind: DataIndexItemKind,
  name: string,
  scope: AttributeScope,
): string {
  const itemId = encodeURIComponent(name);

  if (scope.kind === 'profile') {
    return generatePath('/workspaces/:workspaceId/journeys/data-index/:type/:itemId', {
      workspaceId,
      type: kind === 'event' ? 'events' : 'attributes',
      itemId,
    });
  }

  return generatePath('/workspaces/:workspaceId/journeys/data-index/:type/:parentId/:itemId', {
    workspaceId,
    type: scope.kind === 'relationship' ? 'object-relationship-attributes' : 'object-attributes',
    parentId: String(scope.objectTypeId),
    itemId,
  });
}

/** The browser the landing URL opens on. */
export const DEFAULT_DATA_INDEX_TYPE: DataIndexListType = 'attributes';

/**
 * The kind of row a browser is showing.
 *
 * The URL segment alone does not settle it: choosing an object type narrows
 * the attributes browser without changing the path, so a row's kind follows
 * the scope. Only the events browser is decided by the segment, because
 * events have no object type to narrow by.
 */
export function itemKindOf(type: DataIndexType, scope: AttributeScope): DataIndexItemKind {
  if (type === 'events') {
    return 'event';
  }
  switch (scope.kind) {
    case 'profile':
      return 'attribute';
    case 'object':
      return 'object-attribute';
    case 'relationship':
      return 'object-relationship-attribute';
    default: {
      const _exhaustive: never = scope;
      throw new Error(`unhandled attribute scope: ${String(_exhaustive)}`);
    }
  }
}

/**
 * The two browsers the tab bar offers. The object ones have no tab of their
 * own: they are reached by choosing an object type while on Attributes, so
 * they keep that tab active.
 */
export const DATA_INDEX_TABS = [
  { type: 'attributes', label: 'Attributes' },
  { type: 'events', label: 'Events' },
] as const satisfies readonly { type: DataIndexType; label: string }[];

/**
 * A browser that has a list of its own at `/data-index/:type`. The object
 * segments are not among them: their rows are read by choosing an object type
 * on the attributes list, which writes the query rather than the path, and
 * their own URLs stay with the embedded app.
 */
export type DataIndexListType = (typeof DATA_INDEX_TABS)[number]['type'];

/** The list a URL segment names, or null when it names none of them. */
export function asDataIndexListType(value: string): DataIndexListType | null {
  return DATA_INDEX_TABS.find((tab) => tab.type === value)?.type ?? null;
}

export function activeTabFor(type: DataIndexType): DataIndexType {
  return type === 'events' ? 'events' : 'attributes';
}

/**
 * The object type a browser reads under, as the select encodes it: the object
 * type id, with `:relationship` appended for the relationship side. Profile
 * attributes are `0`, which is also how the wire spells "no object type".
 */
export const PROFILE_SELECTION = '0';

export function encodeScopeSelection(scope: AttributeScope): string {
  if (scope.kind === 'profile') {
    return PROFILE_SELECTION;
  }
  const objectTypeId = String(scope.objectTypeId);
  return scope.kind === 'relationship' ? `${objectTypeId}:relationship` : objectTypeId;
}

/** The scope a select value names, or the profile scope when it names none. */
export function decodeScopeSelection(value: string): AttributeScope {
  const [id, suffix] = value.split(':');
  const objectTypeId = Number(id);
  if (!Number.isInteger(objectTypeId) || objectTypeId <= 0) {
    return { kind: 'profile' };
  }
  return suffix === 'relationship' ? { kind: 'relationship', objectTypeId } : { kind: 'object', objectTypeId };
}
